import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, GitDiff, ShieldCheck, Scales, Eye, ClockCounterClockwise, Lock, ChartLine, FlowArrow, Lightning } from "@phosphor-icons/react";

const fade = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.7 } };

const FEATURES = [
  { icon: FileText, title: "Drafting & Sending", body: "First-draft generation from your precedent. Native DOCX. Send and execute end-to-end with audit-grade traceability." },
  { icon: GitDiff, title: "Contract Comparison", body: "Multi-version diffing across redlines, definitions, and obligations — with rationale and citations." },
  { icon: ShieldCheck, title: "Risk Assessment", body: "Counterparty paper graded against your playbook with severity-scored deviations." },
  { icon: Scales, title: "Critical Clauses", body: "Indemnity, LoL, IP, termination, governing law — surfaced and explained." },
  { icon: Eye, title: "End-to-end Visibility", body: "Portfolio dashboards across matters, counterparties, and obligations." },
  { icon: ClockCounterClockwise, title: "Renewals & Obligations", body: "Track every promise made, every date that matters." },
  { icon: Lock, title: "Privacy & Security", body: "Tenant isolation, encryption at rest and in transit, SOC 2 Type II in progress." },
  { icon: ChartLine, title: "Analytics", body: "Cycle time, deviation rates, negotiation leverage — all measurable." },
];

const WORKFLOWS = [
  {
    n: "01",
    title: "Full Contract Review",
    body: "Upload → extract, clause check, risk score & summary → complete review report delivered in minutes.",
    chips: ["Extract", "Clause check", "Risk score", "Summary"],
    active: "Risk score",
  },
  {
    n: "02",
    title: "Draft & Assess Loop",
    body: "Draft clause → score (≥70) → re-draft if fails → risk assess → if HIGH/MED → redline → notify.",
    chips: ["Draft clause", "Score ≥70", "Risk assess", "Redline"],
    active: "Risk assess",
  },
  {
    n: "03",
    title: "Draft Full Agreement",
    body: "Same loop as #02 applied to a full multi-article contract — redlining weak clauses until the document meets your bar.",
    chips: ["Draft agreement", "Risk assess", "Redline", "Notify"],
    active: "Risk assess",
  },
  {
    n: "04",
    title: "Matter Agent (Cross-Document)",
    body: "Async scan of every document in a matter → detect inter-document conflicts → aggregate findings by severity → notify counsel.",
    chips: ["Async scan", "Conflict detect", "High / Med / Low", "Notify"],
    active: "Conflict detect",
  },
  {
    n: "05",
    title: "Playbook Comparison",
    body: "Contract + playbook → compare each clause against expected positions → flag deviations → deliver a deviation report with severity.",
    chips: ["Ingest contract", "Compare playbook", "Flag deviations", "Report"],
    active: "Compare playbook",
  },
  {
    n: "06",
    title: "Review Pipeline",
    body: "Human-in-the-loop multi-stage review → assign documents → track per-finding status (New → Reviewed → Resolved) → notify at each transition.",
    chips: ["New", "Reviewed", "Resolved", "Notify / stage"],
    active: "Reviewed",
  },
  {
    n: "07",
    title: "Custom Workflow Builder",
    body: "Compose any steps in any order — conditional logic, quality loops, risk-triggered redrafts, and completion connectors into your tools.",
    chips: ["Extract", "Risk assess", "Clause check", "Summarise", "Redline", "Draft clause", "Send for sign", "Email", "Slack", "Teams", "Webhook"],
    active: null,
    wide: true,
  },
];


export default function Product() {
  return (
    <div data-testid="product-page">
      <section className="border-b border-border">
        <div className="container-x py-20 md:py-32 max-w-5xl">
          <p className="overline">The product</p>
          <motion.h1 {...fade} className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.98] mt-5 font-light">
            ContractIQ — <span className="italic text-[#002147]">a workspace</span> for legal teams who measure outcomes.
          </motion.h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl">
            Built with partners and in-house counsel from day one. ContractIQ replaces the patchwork
            of templates, trackers, and email threads with a single intelligent surface.
          </p>
        </div>
      </section>

      <section className="container-x py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-px bg-border border border-border">
          {FEATURES.map((f, i) => (
            <motion.div
              {...fade}
              transition={{ duration: 0.6, delay: i * 0.04 }}
              key={i}
              className="bg-white p-8 hover:bg-secondary/60 transition-colors"
            >
              <f.icon size={22} weight="light" />
              <h3 className="font-serif text-2xl mt-6 tracking-tight">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section data-testid="agentic-workflows" className="bg-secondary/40 border-y border-border">
        <div className="container-x py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="overline">Agentic workflows</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[1.02] mt-4 font-light">
              Seven workflows. <span className="italic text-[#002147]">One composable engine.</span>
            </h2>
            <p className="mt-6 text-base text-muted-foreground leading-relaxed max-w-2xl">
              ContractIQ ships with pre-built agentic workflows for the moves your team already makes — and a builder
              to compose the rest. Every step runs with quality loops, risk triggers, and human-in-the-loop checkpoints.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            {WORKFLOWS.map((w) => (
              <motion.div
                {...fade}
                key={w.n}
                className={`bg-white p-8 md:p-10 ${w.wide ? "md:col-span-2" : ""}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xs text-muted-foreground tracking-[0.2em]">{w.n}</span>
                    <FlowArrow size={20} weight="light" className="text-[#002147]" />
                  </div>
                  {w.n === "07" && (
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 bg-[#002147] text-white">
                      Builder
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-6">{w.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-2xl">{w.body}</p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {w.chips.map((c, i) => {
                    const isActive = w.active === c;
                    return (
                      <span
                        key={i}
                        className={`inline-flex items-center px-3 py-1.5 text-[11px] font-mono tracking-[0.08em] uppercase border ${
                          isActive
                            ? "bg-[#002147] text-white border-[#002147]"
                            : "bg-white text-foreground/75 border-border"
                        }`}
                      >
                        {isActive && <Lightning size={10} weight="fill" className="mr-1.5" />}
                        {c}
                      </span>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          <p className="mt-8 text-xs font-mono tracking-[0.1em] text-muted-foreground">
            ContractIQ · AI-First Contract Lifecycle Management
          </p>
        </div>
      </section>

      <section className="bg-[#0A0A0A] text-white">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="overline text-white/60">Designed with partners</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[1.02] mt-4">
              See ContractIQ <span className="italic text-white/70">on your own paper.</span>
            </h2>
          </div>
          <div className="md:col-span-4">
            <Link
              to="/contact"
              data-testid="product-demo-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#0A0A0A] text-sm font-medium hover:bg-[#94A3B8] transition-colors"
            >
              Request a demo <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
